import React, { useState, useEffect } from "react";

import { Collapse } from "react-collapse";

import { NO_GROUP, ROLES } from "../../utils/constants";

import { longDate } from "../../utils/DateHelper";
import { isEmpty } from "lodash";
import Tippy from "@tippyjs/react";

import { Trans, useTranslation } from "react-i18next";
import LazyImage from "../Shared/LazyImage";
import DeleteButton from "../Buttons/DeleteButton";
import EditButton from "../Buttons/EditButton";
import { isAuthorized } from "../../utils/RolesHelpers";
import { getRoles } from "../../utils/OktaHelpers";
import BaseAlertModal from "../Modals/BaseAlertModal";

const GroupedMaterialsTable = ({
    headers,
    subHeaders,
    data,
    setMaterialModal,
    setIsCreateMaterial,
    setGroupsModal,
    setIsCreateGroup,
    handleDeleteMaterial,
    handleDeleteGroup,
    materialToBeDeleted,
    setMaterialToBeDeleted,
    groupToBeDeleted,
    setGroupToBeDeleted,
    setMaterialToBeEdited,
    setGroupToBeEdited,
}) => {
    const [isCollapsed, setIsCollapsed] = useState({});
    const [deleteModal, setDeleteModal] = useState(false);

    const { t } = useTranslation();

    useEffect(() => {
        let collapsed = {};
        if (data) {
            const defaultGroup = data.find((group) => group.id == NO_GROUP.id);
            if (defaultGroup && defaultGroup.materials.length > 0) {
                collapsed[NO_GROUP.id] = true;
            }
        }
        setIsCollapsed(collapsed);
    }, [data]);

    const handleExpand = (key) => {
        let newCollapse = { ...isCollapsed };
        newCollapse[key] = !isCollapsed[key];
        setIsCollapsed(newCollapse);
    };

    const handleEditMaterialClicked = (material, index) => {
        setIsCreateMaterial(false);
        setMaterialToBeEdited({ data: { ...material }, index });
        setMaterialModal(true);
    };

    const handleDeleteMaterialClicked = (material) => {
        setDeleteModal(true);
        setMaterialToBeDeleted(material);
        setGroupToBeDeleted(null);
    };

    const handleDeleteGroupClicked = (event, group) => {
        event.stopPropagation();
        setDeleteModal(true);
        setGroupToBeDeleted(group);
        setMaterialToBeDeleted(null);
    };

    const handleEditGroupClicked = (event, group, index) => {
        event.stopPropagation();
        setIsCreateGroup(false);
        setGroupToBeEdited({ data: { ...group }, index });
        setGroupsModal(true);
    };

    const hasActionAccess = () => {
        if (isAuthorized([ROLES.support, ROLES.partnerAdmin], getRoles())) {
            return true;
        }
        return false;
    };

    const renderHeadersData = () => {
        return (
            headers &&
            headers.map((header, index) => {
                if (!header.role) {
                    return (
                        <th
                            key={index}
                            scope="col"
                            className="px-6 py-3 text-center text-xs text-gray-500 uppercase tracking-wider"
                        >
                            <div className="flex items-center justify-center">
                                {header.element || (
                                    <span className="ml-2">{header.label}</span>
                                )}
                            </div>
                        </th>
                    );
                }
            })
        );
    };

    const renderSubHeadersData = (group) => {
        if (!isEmpty(group.materials)) {
            return (
                subHeaders &&
                subHeaders.map((header, index) => {
                    if (!header.role) {
                        return (
                            <th
                                key={index}
                                scope="col"
                                className="px-6 py-3 text-center text-xs text-gray-500 uppercase tracking-wider"
                            >
                                <div className="flex items-center justify-center">
                                    <span>{header.label}</span>
                                </div>
                            </th>
                        );
                    }
                })
            );
        } else {
            return (
                <th
                    scope="col"
                    className="px-6 py-3 text-center text-xs text-gray-500 uppercase tracking-wider"
                >
                    <div className="flex items-center justify-center">
                        <span>
                            <Trans i18nKey="noMaterialsFoundGroup">
                                This group got no materials
                            </Trans>
                        </span>
                    </div>
                </th>
            );
        }
    };

    const isNoGroupReturn = (isNoGroup, returnedData) => {
        return isNoGroup ? returnedData : "";
    };

    const renderRowsData = () => {
        return (
            data &&
            data.map((group, index) => {
                const isNoGroup = group.id === NO_GROUP.id;
                return (
                    <React.Fragment key={`${index}`}>
                        <tr
                            className={`tr-ele cursor-pointer ${
                                index % 2 === 0 ? "even" : "odd"
                            }`}
                            onClick={() => handleExpand(group.id)}
                        >
                            <td className="px-6 py-3 whitespace-wrap">
                                <div className="flex items-center">
                                    <i
                                        className={`fas fa-caret-${
                                            isCollapsed[group.id]
                                                ? "down"
                                                : "right"
                                        } mr-2`}
                                    />
                                    <LazyImage
                                        src={
                                            group?.image ||
                                            require("../../assets/images/group-material.png")
                                                .default
                                        }
                                        className="table-user-img"
                                        alt="group image"
                                    />
                                    <span className="user-name-area text-left">
                                        {group.group}
                                    </span>
                                </div>
                            </td>
                            <td className="px-6 py-3 whitespace-wrap">
                                {!group.description ? (
                                    "---"
                                ) : (
                                    <Tippy
                                        content={group.description}
                                        placement="top"
                                        animation="scale"
                                    >
                                        <span
                                            tabIndex="0"
                                            className="tooltip-container"
                                        >
                                            {group.description}
                                        </span>
                                    </Tippy>
                                )}
                            </td>
                            <td className="px-6 py-3 whitespace-wrap">
                                {isNoGroupReturn(isNoGroup, "---") ||
                                    longDate(group.createdAt)}
                            </td>
                            <td className="px-6 py-3 whitespace-wrap">
                                {isNoGroupReturn(isNoGroup, "---") || (
                                    <Tippy
                                        content={group.createdBy}
                                        placement="top"
                                        animation="scale"
                                    >
                                        <span
                                            tabIndex="0"
                                            className="description-oneline tooltip-container"
                                        >
                                            {group.createdBy}
                                        </span>
                                    </Tippy>
                                )}
                            </td>
                            <td className="px-6 py-3 whitespace-wrap">
                                {isNoGroupReturn(isNoGroup, "---") ||
                                    group.organization}
                            </td>

                            {!headers[headers?.length - 1]?.role && (
                                <td className="px-6 py-3 whitespace-wrap">
                                    {!isNoGroup && (
                                        <div className="flex items-center justify-center">
                                            <DeleteButton
                                                onClick={(event) => {
                                                    handleDeleteGroupClicked(
                                                        event,
                                                        group
                                                    );
                                                }}
                                                disabled={isNoGroupReturn(
                                                    isNoGroup,
                                                    "disabled"
                                                )}
                                                tippy={true}
                                            />
                                            <EditButton
                                                onClick={(event) => {
                                                    handleEditGroupClicked(
                                                        event,
                                                        group,
                                                        index
                                                    );
                                                }}
                                                disabled={isNoGroupReturn(
                                                    isNoGroup,
                                                    "disabled"
                                                )}
                                                tippy={true}
                                            />
                                        </div>
                                    )}
                                </td>
                            )}
                        </tr>
                        <tr
                            hidden={!isCollapsed[group.id]}
                            key={`inner-${index}`}
                        >
                            <td colSpan={headers.length} className="bg-white">
                                <Collapse isOpened={isCollapsed[group.id]}>
                                    <div className="table-responsive overflow-hidden border-gray-200 sm:rounded-lg small-scroll-x small-scroll-y px-8 pb-4">
                                        <table className="table-striped min-w-full divide-y divide-gray-200 text-xs text-center">
                                            <thead>
                                                <tr className="bg-white">
                                                    {renderSubHeadersData(
                                                        group
                                                    )}
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {renderSubRowsData(group)}
                                            </tbody>
                                        </table>
                                    </div>
                                </Collapse>
                            </td>
                        </tr>
                    </React.Fragment>
                );
            })
        );
    };

    const renderSubRowsData = (group) => {
        return (
            group.materials &&
            group.materials.map((row, index) => {
                return (
                    <tr key={index} className="tr-ele">
                        <td className="px-6 py-3 whitespace-wrap">
                            <div className="flex items-center">
                                <LazyImage
                                    src={
                                        row?.image ||
                                        require("../../assets/images/material.png")
                                            .default
                                    }
                                    className="table-user-img"
                                    alt="material image"
                                />
                                {row.name}
                            </div>
                        </td>
                        <td className="px-6 py-3 whitespace-wrap">
                            {row.technicalName || "---"}
                        </td>
                        <td className="px-6 py-3 whitespace-wrap">
                            {!row.description ? (
                                "---"
                            ) : (
                                <Tippy
                                    content={row.description}
                                    placement="top"
                                    animation="scale"
                                >
                                    <span
                                        tabIndex="0"
                                        className="tooltip-container"
                                    >
                                        {row.description}
                                    </span>
                                </Tippy>
                            )}
                        </td>
                        <td className="px-6 py-3 whitespace-wrap">
                            {longDate(row.createdAt)}
                        </td>

                        <td className="px-6 py-3 whitespace-wrap">
                            {!row.createdBy ? (
                                "---"
                            ) : (
                                <Tippy
                                    content={row.createdBy}
                                    placement="top"
                                    animation="scale"
                                >
                                    <span
                                        tabIndex="0"
                                        className="tooltip-container"
                                    >
                                        {row.createdBy}
                                    </span>
                                </Tippy>
                            )}
                        </td>

                        {hasActionAccess() && (
                            <td className="px-6 py-3 whitespace-wrap">
                                <div className="flex items-center justify-center">
                                    <DeleteButton
                                        disabled={row.systemGenerated}
                                        hide={!hasActionAccess()}
                                        onClick={() => {
                                            handleDeleteMaterialClicked({
                                                ...row,
                                                materialGroupId: group.id,
                                                materialGroupName: group.group,
                                            });
                                        }}
                                        tippy={true}
                                    />

                                    <EditButton
                                        hide={!hasActionAccess()}
                                        onClick={() => {
                                            handleEditMaterialClicked(
                                                {
                                                    ...row,
                                                    materialGroupId: group.id,
                                                    materialGroupName:
                                                        group.group,
                                                },
                                                index
                                            );
                                        }}
                                        tippy={true}
                                    />
                                </div>
                            </td>
                        )}
                    </tr>
                );
            })
        );
    };

    return (
        <>
            <div className="flex flex-col mb-5">
                <div className="table-responsive shadow overflow-hidden border-b border-gray-200 sm:rounded-lg small-scroll-x small-scroll-y">
                    <table className="table-striped custom min-w-full divide-y divide-gray-200 text-xs text-center">
                        <thead>
                            <tr>{renderHeadersData()}</tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {renderRowsData()}
                        </tbody>
                    </table>
                </div>
            </div>

            <BaseAlertModal
                isVisible={deleteModal}
                setIsVisible={setDeleteModal}
                title={
                    groupToBeDeleted ? t("deleteGroup") : t("deleteMaterial")
                }
                message={
                    groupToBeDeleted
                        ? t("deleteGroupMessage", {
                              toBeDeleted: groupToBeDeleted?.group,
                          })
                        : t("deleteMaterialMessage", {
                              toBeDeleted: materialToBeDeleted?.name,
                          })
                }
                action={{
                    submitAction: groupToBeDeleted
                        ? handleDeleteGroup
                        : handleDeleteMaterial,
                }}
                type="delete"
            />
        </>
    );
};

export default GroupedMaterialsTable;
